<script>
    import {PATH_PREFIX} from '../../../apiUrls.js';
  import { onMount } from 'svelte';
  import { sgettext } from '../../../i18n.js';
  import Panel from '../../Layout/Panel.svelte';
  import { primaryColour } from '../../styles/theme.js';
  import ActivityVersionMaterialRisks from './MaterialRisks/ActivityVersionMaterialRisks.svelte';
  import ActivityVersionControls from './Controls/ActivityVersionControls.svelte';
  import ActivityVersionItems from './Items/ActivityVersionItems.svelte';
  import ActivityVersionAppendix from './Items/ActivityVersionAppendix.svelte';
  import { activityVersion } from '../workspacesStore';

  export let activityVersionId;
  export let branchId;
  export let valid = true;

  let validMaterialRisks = true;
  let validAdditionalControls = true;
  let validStandardPPE = true;
  $: valid = validMaterialRisks && validAdditionalControls && validStandardPPE;

  export let userPermissions = [];
  let isEditable = permission =>{
    if($activityVersion?.activity_type == "Asset Integrity"){
        return $activityVersion?.isEditable && userPermissions.includes("AIES_ACTIVITY_CREATE_AND_EDIT")
    } else {
        $activityVersion?.isEditable && userPermissions.includes(permission);
    }
  }

  // This does not work because $activityVersion does not contain the required fields.
  let getStatus = activityVersion => {
    if(activityVersion) {
      if (activityVersion.template) {
        return sgettext(activityVersion.template_status);
      } else {
        if (activityVersion.manually_changed_instance_status == 1) { // MANUALLY_EXPIRED
          return sgettext('$$MANUALLY_COMPLETED$$');
        } else if (activityVersion.manually_changed_instance_status == 2) { // MANUALLY_COMPLETED
          return sgettext('$$MANUALLY_EXPIRED$$');
        } else if (activityVersion.expired) {
          return sgettext('$$EXPIRED$$');
        } else if (activityVersion.end_time) {
          return sgettext('$$COMPLETE$$');
        } else if (activityVersion.start_time) {
          return sgettext('$$PROGRESS_1$$');
        } else {
          return sgettext('$$NOT_STARTED_1$$');
        }
      }
    }
  };
  $: activityVersionName = $activityVersion ? `: ${$activityVersion.name} ` : '';

  export let leftColumnWidth;
  let isWaiting = true;
  let materialRisks = [];
  let additionalControls = [];
  let toolsConsumablesEquipment = [];
  let appendices = []

  async function retrieveMaterialRisks() {
    isWaiting = true;
    let apiUrl = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/material-risks-readonly/`;
    const response = await fetch(apiUrl);
    if (response.ok) {
      const json = await response.json();
      if (json) {
        materialRisks = json.results;
      }
    }
    isWaiting = false;
  }
  async function retrieveAdditionalControls() {
    isWaiting = true;
    let apiUrl = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/controls-readonly/`;
    const response = await fetch(apiUrl);
    if (response.ok) {
      const json = await response.json();
      if (json) {
        additionalControls = json.results;
      }
    }
    isWaiting = false;
  }
  async function retrieveToolsConsumablesEquipment() {
    isWaiting = true;
    let apiUrl = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/tools-consumables-equipment-readonly/`;
    const response = await fetch(apiUrl);
    if (response.ok) {
      const json = await response.json();
      if (json) {
        toolsConsumablesEquipment = json.results;
      }
    }
    isWaiting = false;
  }
  async function retrieveAppendices() {
    isWaiting = true;
    let apiUrl = `${PATH_PREFIX}/sw/v2/api/activity_version/${activityVersionId}/appendix-readonly/`;
    const response = await fetch(apiUrl);
    if (response.ok) {
      const json = await response.json();
      if (json) {
        appendices = json.results;
      }
    }
    isWaiting = false;
  }

  onMount(async () => {
    await retrieveMaterialRisks();
    await retrieveAdditionalControls();
    await retrieveToolsConsumablesEquipment();
    await retrieveAppendices();
  });
</script>

<Panel
  title="{sgettext('$$ACTIVITY_CONTENT$$')} {activityVersionName} ({getStatus(
    $activityVersion
  )})"
  showTitleExclamationCircle={!valid}
  collapsible={false}
  padded={false}
  hideOverflow={true}
  headerColour={primaryColour}
  showEllipsis={true}
  rightPanelWidth={window.screen.width - leftColumnWidth}
  style="background-color: #e9ecef"
>
  <div class="slot-body" slot="body">
    <div class="container-fluid d-flex flex-column p-4 wrapper">
      <div style="background-color:#d9edf7;" class="mb-2">
        <p class="mt-3 ml-3" style="font-size:14px;">
          <i class="fa fa-info-circle fa-lg text-blue" />
          {sgettext(
            '$$PLEASE_NOTE_CONTENT_ENTERED_POPULATE_PDF$$'
          )}
        </p>
      </div>
      <h3>
        {sgettext('$$ACTIVITY_CONTENT$$')}
        {activityVersionName}
      </h3>
      {#if $activityVersion}
        <ActivityVersionMaterialRisks
          title={sgettext('$$MATERIAL_RISKS$$')}
          {activityVersionId}
          {branchId}
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_MATERIAL_RISKS')}
          {isWaiting}
          retrievedItems={materialRisks}
          bind:valid={validMaterialRisks}
        />
        <ActivityVersionControls
          title={sgettext('$$ADDITIONAL_CONTROLS$$')}
          {activityVersionId}
          {branchId}
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_ADDITIONAL_CONTROLS')}
          {isWaiting}
          retrievedItems={additionalControls}
          bind:valid={validAdditionalControls}
        />
        <ActivityVersionControls
          title={sgettext('$$STANDARD_PPE$$')}
          {activityVersionId}
          {branchId}
          isPPE={true}
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_STANDARD_PPE')}
          {isWaiting}
          retrievedItems={additionalControls}
          bind:valid={validStandardPPE}
        />
        <ActivityVersionItems
          title={sgettext('$$SPECIALIST_TOOLING$$')}
          {activityVersionId}
          itemType="TOOL"
          urlSlug="tools"
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_TOOLS')}
          {isWaiting}
          retrievedItems={toolsConsumablesEquipment}
        />
        <ActivityVersionItems
          title={sgettext('$$EQUIPMENT$$')}
          {activityVersionId}
          itemType="EQUIPMENT"
          urlSlug="equipment"
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_EQUIPMENT')}
          {isWaiting}
          retrievedItems={toolsConsumablesEquipment}
        />
        <ActivityVersionItems
          title={sgettext('$$CONSUMABLES$$')}
          {activityVersionId}
          itemType="CONSUMABLE"
          urlSlug="consumables"
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_CONSUMABLES')}
          {isWaiting}
          retrievedItems={toolsConsumablesEquipment}
        />
        <ActivityVersionAppendix
          title={sgettext('$$APPENDICES$$')}
          {activityVersionId}
          urlSlug="appendix"
          editable={isEditable('ACTIVITY_TEMPLATE_EDIT_CONSUMABLES')}
          {isWaiting}
          retrievedItems={appendices}
        />
      {/if}
    </div>
  </div>
</Panel>

<style>
  .slot-body {
    height: 100%;
  }

  .wrapper {
    background-color: #fff;
  }

  .container-fluid {
    padding-bottom: 7.5rem !important;
  }

  .text-blue {
    color: #09c !important;
  }
</style>
